/**
 * Les gestes de « Ma journée » : où mène chaque ligne, les boîtes de l'accueil, et les deux écritures
 * qu'on y fait (annuler un retard signalé, annuler un remplacement demandé).
 *
 * Où mène chaque geste, comme l'ancien accueil :
 *  - « Rejoindre » → `pre-call` sur la séance (l'ancien « GO ! » du jour) ; « Préparer » →
 *    `tutor/prepare-live` ;
 *  - une séance touchée : sa boîte si elle porte déjà une demande (`useTutorSessionNotice`), sinon
 *    `move-session` (l'ancien « GÉRER LES RDV ») ;
 *  - « Toute la semaine » → `tutor/lives` (l'ancienne modale « VOIR TOUS », devenue une route) ;
 *  - l'offre → l'onglet « Offres de cours » : « Accepter » y ouvre sa confirmation, « Voir » l'offre,
 *    « Toutes les offres » l'onglet seul ;
 *  - une ligne de « À faire » → sa route (`tutorTodoAction`) ; Stripe : l'adresse de son formulaire
 *    se demande, puis s'ouvre dans le même onglet sur le web (l'ancienne bannière) ;
 *  - « Mes paiements » → le tableau Stripe (l'ancien `StripeExpressDashboardButton`), « Historique
 *    des lives » → `tutor-lives-history`.
 * Chaque route d'un écran pas encore câblé rend son ancien écran (`tutorSpaceBridge`).
 *
 * Les écritures : `cancelTutorDelay` et `cancelSubstitution`, puis la relecture du compte et des
 * lives (l'ancienne carte) ; un refus 400 d'annuler un remplacement (lié à une absence sur plusieurs
 * séances) ouvre « Impossible d'annuler », et « Contacter le support » ouvre le WhatsApp de l'équipe.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer } from "react"
import { Platform } from "react-native"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { hideResetModal } from "../../../../../../data/redux/actions/tutorMonthlyScore"
import { skillSetsService } from "../../../../../../data/services/skillSetsService"
import { tutorMonthlyScoreService } from "../../../../../../data/services/tutorMonthlyScoreService"
import { getFromStorage, setToStorage } from "../../../../../../data/utility/asyncStorageUtils"
import { type UITutorHomeSession } from "../../../../adapters/tutor/Home/tutorSessionAdapter"
import {
    TUTOR_AVAILABILITY_REMINDER_STORAGE_KEY,
    tutorAvailabilityReminderDue,
    tutorAvailabilityReminderMonth,
    tutorTodoAction,
} from "../../../../adapters/tutor/Home/tutorTodoAdapter"
import { type TutorHomeViewModel } from "../../../../view-models/tutor/Home/tutorHomeViewModel"
import { type UITutorTodo } from "../../../types/tutorCoquilleAccueil"
import { type UITutorOffer } from "../../../types/tutorDispoOffres"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { useApiFetch } from "../../../hooks/useApiFetch"
import { useStripeDashboard } from "../../../hooks/useStripeDashboard"
import { useTutorSessionNotice } from "../Lives/useTutorSessionNotice"
import { useOpenTutorSupport } from "../useOpenTutorSupport"
import { initialTutorHomeUiState, tutorHomeUiReducer } from "./tutorHomeUiState"

type PushNavigation = {
    push: (route: string, params?: object) => void
    navigate: (route: string, params?: object) => void
}

/** L'écran d'où partent les gestes de l'accueil, en propriété `surface` de leur mesure. */
const SURFACE = "tutor_home"

/** Le formulaire Stripe s'ouvre dans le même onglet sur le web, comme l'ancienne bannière. */
function openStripeForm(url: string | null | undefined) {
    if (!url) return
    if (Platform.OS === "web" && typeof window !== "undefined") window.open(url, "_self")
    else openExternalUrl(url)
}

/** `onFailure` : un geste refusé (le formulaire ou le tableau Stripe), dit par la page (`useTutorHomeData`). */
export function useTutorHomeState(vm: TutorHomeViewModel, onFailure?: (error: unknown) => void) {
    const navigation = useNavigation() as unknown as PushNavigation
    const dispatch = useDispatch()
    const [ui, dispatchUi] = useReducer(tutorHomeUiReducer, initialTutorHomeUiState)
    const openSupport = useOpenTutorSupport()
    const push = useCallback((route: string, params?: object) => navigation.push(route, params), [navigation])

    // ── Le rappel mensuel de « Ma dispo » : la même clé d'appareil que l'ancien, le mois écrit une fois
    // la boîte montrée.
    const { availabilityNudge } = vm
    useEffect(() => {
        if (!availabilityNudge) return
        let mounted = true
        void getFromStorage(TUTOR_AVAILABILITY_REMINDER_STORAGE_KEY).then((stored: string | null) => {
            if (!mounted || !tutorAvailabilityReminderDue(availabilityNudge, stored)) return
            dispatchUi({ type: "OPEN_REMINDER" })
            void setToStorage(TUTOR_AVAILABILITY_REMINDER_STORAGE_KEY, tutorAvailabilityReminderMonth())
        })
        return () => {
            mounted = false
        }
    }, [availabilityNudge])

    // ── La boîte d'une séance et ses écritures (`useTutorSessionNotice`, la même que la semaine des lives).
    const sessionNotice = useTutorSessionNotice(SURFACE)
    const { open: openSessionNotice } = sessionNotice
    const selectSession = useCallback(
        (session: UITutorHomeSession) => {
            if (!openSessionNotice(session)) push("move-session", { liveId: session.id })
        },
        [push, openSessionNotice]
    )

    // ── Stripe : le formulaire (la ligne de « À faire ») et le tableau de bord (« Mes paiements »).
    const { fetchWith: fetchStripeForm } = useApiFetch<{ url?: string }>({
        service: skillSetsService.getStripeFormUrl,
        fetchOnMount: false,
        skipWhilePending: true,
        idleOnError: true,
        onSuccess: (data) => openStripeForm(data?.url),
        onError: onFailure,
    })
    const payments = useStripeDashboard(onFailure)

    const onTodoPress = useCallback(
        (todo: UITutorTodo) => {
            const action = tutorTodoAction(todo)
            if (action == null) return
            if (action.type === "stripe_form_url") fetchStripeForm({ email: vm.email })
            else push(action.route, action.params)
        },
        [push, fetchStripeForm, vm.email]
    )

    const openOffers = useCallback(() => navigation.navigate("proposals"), [navigation])
    // L'onglet des offres ouvre l'offre elle-même (`TutorOffersScreen`).
    const openOffer = useCallback(
        (offer: UITutorOffer, offerStep: "detail" | "confirm") =>
            navigation.navigate("proposals", { offerKind: offer.kind, offerId: offer.id, offerStep }),
        [navigation]
    )

    return {
        onJoinSession: (session: UITutorHomeSession) => push("pre-call", { liveSessionId: session.id }),
        onPrepareSession: () => push("tutor/prepare-live"),
        onSelectSession: selectSession,
        onOpenWeek: () => push("tutor/lives"),
        onAcceptOffer: (offer: UITutorOffer) => openOffer(offer, "confirm"),
        onOpenOffer: (offer: UITutorOffer) => openOffer(offer, "detail"),
        onOpenOffers: openOffers,
        onTodoPress,
        onOpenPayments: payments.open,
        openingPayments: payments.opening,
        onOpenLivesHistory: () => push("tutor-lives-history"),

        availabilityReminder: ui.availabilityReminder,
        onConfirmAvailabilityReminder: () => {
            dispatchUi({ type: "CLOSE_REMINDER" })
            push("tutor/ma-dispo")
        },
        onDismissAvailabilityReminder: () => dispatchUi({ type: "CLOSE_REMINDER" }),
        onCloseMonthReset: () => {
            dispatch(apiRequest(tutorMonthlyScoreService.markResetModalShown) as never)
            dispatch(hideResetModal() as never)
        },
        sessionNotice: sessionNotice.sessionNotice,
        onCancelSessionRequest: sessionNotice.cancelRequest,
        onContactSupport: () => openSupport(),
        onCloseSessionNotice: sessionNotice.close,
    }
}
